import React, { useState, useEffect } from 'react';
import { Briefcase, FileText, Calendar, Users, Mail, Phone, Building2, Eye, X, Trash2, UserCheck, Clock, Award, DollarSign, MessageSquare, CheckCircle, UserPlus, Sparkles } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';

interface CustomerProfile {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  companies: {
    name: string;
  };
}

interface ApplicationQuestion {
  question: string;
}

interface QuestionAnswer {
  question: string;
  answer: string;
}

interface JobPosting {
  id: string;
  customer_id: string;
  requisition_number: string;
  job_title: string;
  hire_deadline_date: string;
  worker_type: string;
  job_description: string;
  supervisory_organization: string;
  job_family: string;
  skills_qualifications: string;
  compensation_amount: number;
  comp_grade: string;
  status: string;
  application_questions?: ApplicationQuestion[];
  created_at: string;
  customer_profile?: CustomerProfile;
  application_count?: number;
}

interface JobApplication {
  id: string;
  job_posting_id: string;
  consultant_id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  company?: string;
  title?: string;
  country_region: string;
  experience_years: string;
  workday_modules: string;
  certifications?: string;
  availability: string;
  hourly_rate: string;
  message: string;
  cover_letter?: string;
  status: string;
  created_at: string;
  applied_at?: string;
  reviewed_by?: string;
  reviewed_at?: string;
  admin_notes?: string;
  resume_url?: string;
  question_answers?: QuestionAnswer[];
}

interface PositionRequirement {
  positions: string;
  type: string;
}

interface ServiceRequirement {
  id: string;
  customer_id: string;
  project_title: string;
  position_requirements: PositionRequirement[];
  issues_and_modules: string;
  project_requirements: string;
  implementation_stages: string;
  implementation_partner: string;
  number_of_employees: string;
  go_live_date: string;
  hire_deadline_date: string;
  enhancements: string;
  created_at: string;
  customer_profile?: CustomerProfile;
}

interface WorkdayCustomer {
  id: string;
  first_name: string;
  last_name: string;
  companies: { name: string } | null;
}

export default function JobPostingsAndServices() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'jobs' | 'services'>('jobs');
  const [jobPostings, setJobPostings] = useState<JobPosting[]>([]);
  const [serviceRequirements, setServiceRequirements] = useState<ServiceRequirement[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState<JobPosting | null>(null);
  const [selectedService, setSelectedService] = useState<ServiceRequirement | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ type: 'job' | 'service', id: string } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [applications, setApplications] = useState<JobApplication[]>([]);
  const [loadingApplications, setLoadingApplications] = useState(false);
  const [activeJobTab, setActiveJobTab] = useState<'details' | 'applications'>('details');
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);
  const [showAssignPopup, setShowAssignPopup] = useState<string | null>(null);
  const [workdayCustomers, setWorkdayCustomers] = useState<WorkdayCustomer[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);

      const { data: jobs, error: jobError } = await supabase
        .from('workday_client_job_postings')
        .select(`
          *,
          workday_client_profiles!workday_client_job_postings_customer_id_fkey (
            id,
            first_name,
            last_name,
            email,
            phone,
            companies (
              name
            )
          )
        `)
        .order('created_at', { ascending: false });

      if (jobError) throw jobError;

      // Fetch application counts for each job
      const { data: applicationCounts, error: countError } = await supabase
        .from('job_applications')
        .select('job_posting_id');

      if (countError) console.error('Error fetching application counts:', countError);

      const countMap: { [key: string]: number } = {};
      applicationCounts?.forEach(app => {
        countMap[app.job_posting_id] = (countMap[app.job_posting_id] || 0) + 1;
      });

      const { data: services, error: serviceError } = await supabase
        .from('workday_client_service_requirements')
        .select(`
          *,
          workday_client_profiles!workday_client_service_requirements_customer_id_fkey (
            id,
            first_name,
            last_name,
            email,
            phone,
            companies (
              name
            )
          )
        `)
        .order('created_at', { ascending: false });

      if (serviceError) throw serviceError;

      const formattedJobs = jobs?.map(job => ({
        ...job,
        customer_profile: job.workday_client_profiles,
        application_count: countMap[job.id] || 0
      })) || [];

      const formattedServices = services?.map(service => ({
        ...service,
        customer_profile: service.workday_client_profiles
      })) || [];

      setJobPostings(formattedJobs);
      setServiceRequirements(formattedServices);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchApplications = async (jobPostingId: string) => {
    try {
      setLoadingApplications(true);
      const { data, error } = await supabase
        .from('job_applications')
        .select('*')
        .eq('job_posting_id', jobPostingId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setApplications(data || []);
    } catch (error) {
      console.error('Error fetching applications:', error);
    } finally {
      setLoadingApplications(false);
    }
  };

  const updateApplicationStatus = async (applicationId: string, newStatus: string) => {
    try {
      setUpdatingStatus(applicationId);
      const { error } = await supabase
        .from('job_applications')
        .update({
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', applicationId);

      if (error) throw error;

      // Update local state
      setApplications(applications.map(app =>
        app.id === applicationId ? { ...app, status: newStatus } : app
      ));
    } catch (error) {
      console.error('Error updating application status:', error);
      alert('Failed to update application status');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const fetchWorkdayCustomers = async () => {
    try {
      setLoadingCustomers(true);
      const { data, error } = await supabase
        .from('workday_client_profiles')
        .select(`
          id,
          first_name,
          last_name,
          companies (
            name
          )
        `)
        .order('first_name', { ascending: true });

      if (error) throw error;
      setWorkdayCustomers(data || []);
    } catch (error) {
      console.error('Error fetching workday customers:', error);
    } finally {
      setLoadingCustomers(false);
    }
  };

  const handleAssignToClient = async (consultantId: string, customerId: string) => {
    try {
      setAssigning(true);

      // Here you would implement the logic to assign the consultant to the client
      // This might involve updating the consultant profile or creating a relationship record
      const { error } = await supabase
        .from('consultant_profiles')
        .update({
          current_client_id: customerId,
          assignment_status: 'assigned'
        })
        .eq('id', consultantId);

      if (error) throw error;

      alert('Consultant successfully assigned to client!');
      setShowAssignPopup(null);
    } catch (error) {
      console.error('Error assigning consultant:', error);
      alert('Failed to assign consultant to client');
    } finally {
      setAssigning(false);
    }
  };

  const openAssignPopup = (consultantId: string) => {
    setShowAssignPopup(consultantId);
    if (workdayCustomers.length === 0) {
      fetchWorkdayCustomers();
    }
  };

  const handleDeleteJob = async (id: string) => {
    try {
      setDeleting(true);
      console.log('Attempting to delete job posting with id:', id);

      const { data: { session } } = await supabase.auth.getSession();
      console.log('Current session:', session ? 'Valid' : 'No session');
      console.log('User ID:', session?.user?.id);
      console.log('User email:', session?.user?.email);

      if (!session) {
        alert('Session expired. Please log in again.');
        setDeleting(false);
        return;
      }

      const { error, data } = await supabase
        .from('workday_client_job_postings')
        .delete()
        .eq('id', id)
        .select();

      console.log('Delete response:', { data, error });

      if (error) {
        console.error('Supabase error details:', error);
        throw error;
      }

      console.log('Successfully deleted job posting');
      setJobPostings(jobPostings.filter(job => job.id !== id));
      setDeleteConfirm(null);
    } catch (error: any) {
      console.error('Error deleting job posting:', error);
      alert(`Failed to delete job posting: ${error?.message || 'Unknown error'}`);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteService = async (id: string) => {
    try {
      setDeleting(true);
      console.log('Attempting to delete service requirement with id:', id);

      const { data: { session } } = await supabase.auth.getSession();
      console.log('Current session:', session ? 'Valid' : 'No session');

      if (!session) {
        alert('Session expired. Please log in again.');
        setDeleting(false);
        return;
      }

      const { error, data } = await supabase
        .from('workday_client_service_requirements')
        .delete()
        .eq('id', id)
        .select();

      console.log('Delete response:', { data, error });

      if (error) {
        console.error('Supabase error details:', error);
        throw error;
      }

      console.log('Successfully deleted service requirement');
      setServiceRequirements(serviceRequirements.filter(service => service.id !== id));
      setDeleteConfirm(null);
    } catch (error: any) {
      console.error('Error deleting service requirement:', error);
      alert(`Failed to delete service requirement: ${error?.message || 'Unknown error'}`);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-gray-900">Job Postings & Service Requirements</h1>
        </div>

        <div className="bg-white border-b border-gray-200">
          <div className="flex gap-4">
            <button
              onClick={() => setActiveTab('jobs')}
              className={`px-6 py-3 font-medium transition-colors border-b-2 ${
                activeTab === 'jobs'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <Briefcase className="w-5 h-5 inline mr-2" />
              Job Postings ({jobPostings.length})
            </button>
            <button
              onClick={() => setActiveTab('services')}
              className={`px-6 py-3 font-medium transition-colors border-b-2 ${
                activeTab === 'services'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <FileText className="w-5 h-5 inline mr-2" />
              Workday Development Requirements ({serviceRequirements.length})
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : activeTab === 'jobs' ? (
          <div className="space-y-4">
            {jobPostings.length === 0 ? (
              <div className="bg-white rounded-lg p-8 text-center border border-gray-200">
                <Briefcase className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-600">No job postings yet.</p>
              </div>
            ) : (
              jobPostings.map((job) => (
                <div
                  key={job.id}
                  className="bg-white rounded-lg p-6 border border-gray-200 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-semibold text-gray-900">
                          {job.job_title || `Requisition: ${job.requisition_number}`}
                        </h3>
                        {job.application_count !== undefined && job.application_count > 0 && (
                          <span className="px-3 py-1 rounded-full bg-purple-100 text-purple-800 text-sm font-semibold flex items-center gap-1">
                            <UserCheck className="w-4 h-4" />
                            {job.application_count} {job.application_count === 1 ? 'Application' : 'Applications'}
                          </span>
                        )}
                      </div>
                      {job.job_title && (
                        <div className="text-sm text-gray-600 mb-2">
                          Req #: {job.requisition_number}
                        </div>
                      )}
                      <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
                        <span className="flex items-center">
                          <Calendar className="w-4 h-4 mr-1" />
                          Deadline: {new Date(job.hire_deadline_date).toLocaleDateString()}
                        </span>
                        <span className="px-2 py-1 rounded-full bg-blue-100 text-blue-800 text-xs font-medium">
                          {job.worker_type}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          job.status === 'open' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {job.status}
                        </span>
                      </div>
                      {job.customer_profile && (
                        <div className="flex items-center text-sm text-gray-600 mt-2">
                          <Users className="w-4 h-4 mr-1" />
                          <span>Posted by: {job.customer_profile.first_name} {job.customer_profile.last_name}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setSelectedJob(job)}
                        className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        View Details
                      </button>
                      <button
                        onClick={() => setDeleteConfirm({ type: 'job', id: job.id })}
                        className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors flex items-center"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </button>
                    </div>
                  </div>

                  {job.customer_profile && (
                    <div className="bg-gray-50 rounded-lg p-4 mb-4">
                      <h4 className="font-semibold text-gray-900 mb-2">Customer Information</h4>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div className="flex items-center">
                          <Users className="w-4 h-4 text-gray-600 mr-2" />
                          <span className="text-gray-900">{job.customer_profile.first_name} {job.customer_profile.last_name}</span>
                        </div>
                        <div className="flex items-center">
                          <Mail className="w-4 h-4 text-gray-600 mr-2" />
                          <span className="text-gray-900">{job.customer_profile.email}</span>
                        </div>
                        <div className="flex items-center">
                          <Phone className="w-4 h-4 text-gray-600 mr-2" />
                          <span className="text-gray-900">{job.customer_profile.phone}</span>
                        </div>
                        <div className="flex items-center">
                          <Building2 className="w-4 h-4 text-gray-600 mr-2" />
                          <span className="text-gray-900">{job.customer_profile.companies?.name || 'N/A'}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="text-sm text-gray-700">
                    <p className="font-medium mb-1">Job Description:</p>
                    <p className="line-clamp-2">{job.job_description}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {serviceRequirements.length === 0 ? (
              <div className="bg-white rounded-lg p-8 text-center border border-gray-200">
                <FileText className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-600">No development requirements yet.</p>
              </div>
            ) : (
              serviceRequirements.map((service) => (
                <div
                  key={service.id}
                  className="bg-white rounded-lg p-6 border border-gray-200 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">
                        {service.project_title || 'Development Requirement'}
                      </h3>
                      <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
                        <span className="flex items-center">
                          <Calendar className="w-4 h-4 mr-1" />
                          Submitted: {new Date(service.created_at).toLocaleDateString()}
                        </span>
                        {service.number_of_employees && (
                          <span className="px-2 py-1 rounded-full bg-gray-100 text-gray-800 text-xs font-medium">
                            {service.number_of_employees} employees
                          </span>
                        )}
                      </div>
                      {service.customer_profile && (
                        <div className="flex items-center text-sm text-gray-600 mt-2">
                          <Users className="w-4 h-4 mr-1" />
                          <span>Posted by: {service.customer_profile.first_name} {service.customer_profile.last_name}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setSelectedService(service)}
                        className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        View Details
                      </button>
                      <button
                        onClick={() => setDeleteConfirm({ type: 'service', id: service.id })}
                        className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors flex items-center"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </button>
                    </div>
                  </div>

                  {service.customer_profile && (
                    <div className="bg-gray-50 rounded-lg p-4 mb-4">
                      <h4 className="font-semibold text-gray-900 mb-2">Customer Information</h4>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div className="flex items-center">
                          <Users className="w-4 h-4 text-gray-600 mr-2" />
                          <span className="text-gray-900">{service.customer_profile.first_name} {service.customer_profile.last_name}</span>
                        </div>
                        <div className="flex items-center">
                          <Mail className="w-4 h-4 text-gray-600 mr-2" />
                          <span className="text-gray-900">{service.customer_profile.email}</span>
                        </div>
                        <div className="flex items-center">
                          <Phone className="w-4 h-4 text-gray-600 mr-2" />
                          <span className="text-gray-900">{service.customer_profile.phone}</span>
                        </div>
                        <div className="flex items-center">
                          <Building2 className="w-4 h-4 text-gray-600 mr-2" />
                          <span className="text-gray-900">{service.customer_profile.companies?.name || 'N/A'}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {service.project_requirements && (
                    <div className="text-sm text-gray-700">
                      <p className="font-medium mb-1">Project Requirements:</p>
                      <p className="line-clamp-2">{service.project_requirements}</p>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {selectedJob && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <h2 className="text-2xl font-bold text-gray-900">Job Posting Details</h2>
                  {selectedJob.application_count !== undefined && selectedJob.application_count > 0 && (
                    <span className="px-3 py-1 rounded-full bg-purple-100 text-purple-800 text-sm font-semibold flex items-center gap-1">
                      <UserCheck className="w-4 h-4" />
                      {selectedJob.application_count} {selectedJob.application_count === 1 ? 'Application' : 'Applications'}
                    </span>
                  )}
                </div>
                <button
                  onClick={() => {
                    setSelectedJob(null);
                    setActiveJobTab('details');
                    setApplications([]);
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-700 hover:text-gray-900"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="border-b border-gray-200 bg-white">
                <div className="flex gap-4 px-6">
                  <button
                    onClick={() => setActiveJobTab('details')}
                    className={`px-4 py-3 font-medium transition-colors border-b-2 ${
                      activeJobTab === 'details'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Job Details
                  </button>
                  <button
                    onClick={() => {
                      setActiveJobTab('applications');
                      if (applications.length === 0) {
                        fetchApplications(selectedJob.id);
                      }
                    }}
                    className={`px-4 py-3 font-medium transition-colors border-b-2 ${
                      activeJobTab === 'applications'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Applications ({selectedJob.application_count || 0})
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-6">{activeJobTab === 'details' ? (
              <div className="space-y-6">
                {selectedJob.job_title && (
                  <div className="pb-4 border-b border-gray-200">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{selectedJob.job_title}</h3>
                    {selectedJob.customer_profile && (
                      <p className="text-sm text-gray-600">
                        Posted by {selectedJob.customer_profile.first_name} {selectedJob.customer_profile.last_name}
                        {selectedJob.customer_profile.companies?.name && ` from ${selectedJob.customer_profile.companies.name}`}
                      </p>
                    )}
                  </div>
                )}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Requisition Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Requisition Number</p>
                      <p className="font-medium text-gray-900">{selectedJob.requisition_number}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Hire Deadline</p>
                      <p className="font-medium text-gray-900">{new Date(selectedJob.hire_deadline_date).toLocaleDateString()}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Worker Type</p>
                      <p className="font-medium text-gray-900 capitalize">{selectedJob.worker_type}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Status</p>
                      <p className="font-medium text-gray-900 capitalize">{selectedJob.status}</p>
                    </div>
                    {selectedJob.job_family && (
                      <div>
                        <p className="text-sm text-gray-600">Job Family</p>
                        <p className="font-medium text-gray-900">{selectedJob.job_family}</p>
                      </div>
                    )}
                    {selectedJob.supervisory_organization && (
                      <div>
                        <p className="text-sm text-gray-600">Supervisory Organization</p>
                        <p className="font-medium text-gray-900">{selectedJob.supervisory_organization}</p>
                      </div>
                    )}
                    {selectedJob.compensation_amount && (
                      <div>
                        <p className="text-sm text-gray-600">Compensation</p>
                        <p className="font-medium text-gray-900">${selectedJob.compensation_amount.toLocaleString()}</p>
                      </div>
                    )}
                    {selectedJob.comp_grade && (
                      <div>
                        <p className="text-sm text-gray-600">Comp Grade</p>
                        <p className="font-medium text-gray-900">{selectedJob.comp_grade}</p>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Job Description</h3>
                  <p className="text-gray-700 whitespace-pre-wrap">{selectedJob.job_description}</p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Skills & Qualifications</h3>
                  <p className="text-gray-700 whitespace-pre-wrap">{selectedJob.skills_qualifications}</p>
                </div>

                {selectedJob.application_questions && selectedJob.application_questions.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Application Questions</h3>
                    <div className="space-y-3">
                      {selectedJob.application_questions.map((q, index) => (
                        <div key={index} className="bg-gray-50 p-4 rounded-lg">
                          <p className="font-semibold text-gray-900">{index + 1}. {q.question}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selectedJob.customer_profile && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Customer Contact</h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="font-medium text-gray-900">{selectedJob.customer_profile.first_name} {selectedJob.customer_profile.last_name}</p>
                      <p className="text-sm text-gray-600">{selectedJob.customer_profile.email}</p>
                      <p className="text-sm text-gray-600">{selectedJob.customer_profile.phone}</p>
                      <p className="text-sm text-gray-600">{selectedJob.customer_profile.companies?.name}</p>
                    </div>
                  </div>
                )}
              </div>
              ) : (
                <div className="space-y-4">
                  {loadingApplications ? (
                    <div className="flex justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                    </div>
                  ) : applications.length === 0 ? (
                    <div className="text-center py-12">
                      <UserCheck className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600">No applications received yet.</p>
                    </div>
                  ) : (
                    applications.map((application) => (
                      <div
                        key={application.id}
                        className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-xl font-semibold text-gray-900">
                                {application.first_name} {application.last_name}
                              </h3>
                              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                application.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                application.status === 'reviewed' ? 'bg-blue-100 text-blue-800' :
                                application.status === 'shortlisted' ? 'bg-purple-100 text-purple-800' :
                                application.status === 'interview_scheduled' ? 'bg-indigo-100 text-indigo-800' :
                                application.status === 'rejected' ? 'bg-red-100 text-red-800' :
                                application.status === 'hired' ? 'bg-green-100 text-green-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {application.status === 'shortlisted' ? 'PRE-SELECTED' : application.status.replace('_', ' ').toUpperCase()}
                              </span>
                            </div>
                            {application.title && (
                              <p className="text-gray-600 text-sm mb-2">{application.title}</p>
                            )}
                            <div className="flex items-center gap-4 text-sm text-gray-600">
                              <span className="flex items-center">
                                <Mail className="w-4 h-4 mr-1" />
                                {application.email}
                              </span>
                              <span className="flex items-center">
                                <Phone className="w-4 h-4 mr-1" />
                                {application.phone}
                              </span>
                              <span className="flex items-center">
                                <Clock className="w-4 h-4 mr-1" />
                                Applied: {new Date(application.applied_at || application.created_at).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                          <div className="flex flex-col gap-2">
                            <select
                              value={application.status}
                              onChange={(e) => updateApplicationStatus(application.id, e.target.value)}
                              disabled={updatingStatus === application.id}
                              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
                            >
                              <option value="pending">Pending</option>
                              <option value="reviewed">Reviewed</option>
                              <option value="shortlisted">Pre-Selected</option>
                              <option value="interview_scheduled">Interview Scheduled</option>
                              <option value="rejected">Rejected</option>
                              <option value="hired">Hired</option>
                            </select>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                          <div className="bg-gray-50 rounded-lg p-3">
                            <p className="text-xs text-gray-500 mb-1">Experience</p>
                            <p className="font-semibold text-gray-900">{application.experience_years}</p>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-3">
                            <p className="text-xs text-gray-500 mb-1">Hourly Rate</p>
                            <p className="font-semibold text-gray-900 flex items-center">
                              <DollarSign className="w-4 h-4 mr-1" />
                              {application.hourly_rate}
                            </p>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-3">
                            <p className="text-xs text-gray-500 mb-1">Availability</p>
                            <p className="font-semibold text-gray-900">{application.availability}</p>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-3">
                            <p className="text-xs text-gray-500 mb-1">Location</p>
                            <p className="font-semibold text-gray-900">{application.country_region}</p>
                          </div>
                        </div>

                        <div className="mb-4">
                          <div className="flex items-center gap-2 mb-2">
                            <CheckCircle className="w-4 h-4 text-gray-600" />
                            <h4 className="font-semibold text-gray-900">Workday Modules</h4>
                          </div>
                          <p className="text-gray-700 text-sm">{application.workday_modules}</p>
                        </div>

                        {application.certifications && (
                          <div className="mb-4">
                            <div className="flex items-center gap-2 mb-2">
                              <Award className="w-4 h-4 text-gray-600" />
                              <h4 className="font-semibold text-gray-900">Certifications</h4>
                            </div>
                            <p className="text-gray-700 text-sm">{application.certifications}</p>
                          </div>
                        )}

                        {(application.cover_letter || application.message) && (
                          <div className="mb-4">
                            <div className="flex items-center gap-2 mb-2">
                              <MessageSquare className="w-4 h-4 text-gray-600" />
                              <h4 className="font-semibold text-gray-900">Why They're Qualified</h4>
                            </div>
                            <div className="bg-gray-50 rounded-lg p-4">
                              <p className="text-gray-700 text-sm whitespace-pre-wrap">
                                {application.cover_letter || application.message}
                              </p>
                            </div>
                          </div>
                        )}

                        {application.question_answers && application.question_answers.length > 0 && (
                          <div className="mb-4">
                            <div className="flex items-center gap-2 mb-2">
                              <MessageSquare className="w-4 h-4 text-gray-600" />
                              <h4 className="font-semibold text-gray-900">Application Question Responses</h4>
                            </div>
                            <div className="space-y-3">
                              {application.question_answers.map((qa, index) => (
                                <div key={index} className="bg-gray-50 rounded-lg p-4">
                                  <p className="font-medium text-gray-900 text-sm mb-1">{index + 1}. {qa.question}</p>
                                  <p className="text-gray-700 text-sm">{qa.answer}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {application.resume_url && (
                          <div className="mb-4">
                            <a
                              href={application.resume_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm"
                            >
                              <FileText className="w-4 h-4 mr-2" />
                              View Resume
                            </a>
                          </div>
                        )}

                        {application.admin_notes && (
                          <div className="border-t border-gray-200 pt-4 mt-4">
                            <h4 className="font-semibold text-gray-900 text-sm mb-2">Admin Notes</h4>
                            <p className="text-gray-700 text-sm">{application.admin_notes}</p>
                          </div>
                        )}

                        <div className="flex gap-3 mt-4 pt-4 border-t border-gray-200">
                          <button
                            onClick={() => navigate(`/admin/workday-consultants?consultant=${application.consultant_id}`)}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                          >
                            <Sparkles className="w-4 h-4" />
                            View Onboarding
                          </button>
                          <button
                            onClick={() => openAssignPopup(application.consultant_id)}
                            className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                          >
                            <UserPlus className="w-4 h-4" />
                            Hire & Assign to Client
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
              </div>
            </div>
          </div>
        )}

        {showAssignPopup && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">Assign Consultant to Client</h3>
                <button
                  onClick={() => setShowAssignPopup(null)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="p-6">
                <p className="text-gray-600 mb-4">
                  Select a client to assign this consultant to:
                </p>

                {loadingCustomers ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                  </div>
                ) : workdayCustomers.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-600">No clients available</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {workdayCustomers.map((customer) => (
                      <button
                        key={customer.id}
                        onClick={() => handleAssignToClient(showAssignPopup, customer.id)}
                        disabled={assigning}
                        className="w-full text-left p-4 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-semibold text-gray-900">
                              {customer.first_name} {customer.last_name}
                            </p>
                            <p className="text-sm text-gray-600">
                              {customer.companies?.name || 'No company'}
                            </p>
                          </div>
                          <UserPlus className="w-5 h-5 text-blue-500" />
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                <div className="flex gap-3 mt-6 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => setShowAssignPopup(null)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {selectedService && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">Development Requirement Details</h2>
                <button
                  onClick={() => setSelectedService(null)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-700 hover:text-gray-900"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="p-6 space-y-6">
                {selectedService.project_title && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Project Title</h3>
                    <p className="text-gray-700">{selectedService.project_title}</p>
                    {selectedService.customer_profile && (
                      <p className="text-sm text-gray-600 mt-1">
                        Posted by {selectedService.customer_profile.first_name} {selectedService.customer_profile.last_name}
                        {selectedService.customer_profile.companies?.name && ` from ${selectedService.customer_profile.companies.name}`}
                      </p>
                    )}
                  </div>
                )}

                {selectedService.position_requirements && selectedService.position_requirements.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Position Requirements</h3>
                    <div className="space-y-2">
                      {selectedService.position_requirements.map((req, index) => (
                        <div key={index} className="bg-gray-50 p-3 rounded-lg">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-xs text-gray-500">Positions</p>
                              <p className="font-medium text-gray-900">{req.positions || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Type</p>
                              <p className="font-medium text-gray-900">{req.type || 'N/A'}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Implementation Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    {selectedService.number_of_employees && (
                      <div>
                        <p className="text-sm text-gray-600">Number of Employees</p>
                        <p className="font-medium text-gray-900">{selectedService.number_of_employees}</p>
                      </div>
                    )}
                    {selectedService.go_live_date && (
                      <div>
                        <p className="text-sm text-gray-600">Go Live Date</p>
                        <p className="font-medium text-gray-900">{new Date(selectedService.go_live_date).toLocaleDateString()}</p>
                      </div>
                    )}
                    {selectedService.hire_deadline_date && (
                      <div>
                        <p className="text-sm text-gray-600">Hire Deadline Date</p>
                        <p className="font-medium text-gray-900">{new Date(selectedService.hire_deadline_date).toLocaleDateString()}</p>
                      </div>
                    )}
                    {selectedService.implementation_partner && (
                      <div className="col-span-2">
                        <p className="text-sm text-gray-600">Implementation Partner</p>
                        <p className="font-medium text-gray-900">{selectedService.implementation_partner}</p>
                      </div>
                    )}
                  </div>
                </div>

                {selectedService.issues_and_modules && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Workday Issues & Module Needs</h3>
                    <p className="text-gray-700 whitespace-pre-wrap">{selectedService.issues_and_modules}</p>
                  </div>
                )}

                {selectedService.project_requirements && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Project Requirements</h3>
                    <p className="text-gray-700 whitespace-pre-wrap">{selectedService.project_requirements}</p>
                  </div>
                )}

                {selectedService.implementation_stages && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Implementation Stages</h3>
                    <p className="text-gray-700 whitespace-pre-wrap">{selectedService.implementation_stages}</p>
                  </div>
                )}

                {selectedService.enhancements && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Enhancements & Optimizations</h3>
                    <p className="text-gray-700 whitespace-pre-wrap">{selectedService.enhancements}</p>
                  </div>
                )}

                {selectedService.customer_profile && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Customer Contact</h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="font-medium text-gray-900">{selectedService.customer_profile.first_name} {selectedService.customer_profile.last_name}</p>
                      <p className="text-sm text-gray-600">{selectedService.customer_profile.email}</p>
                      <p className="text-sm text-gray-600">{selectedService.customer_profile.phone}</p>
                      <p className="text-sm text-gray-600">{selectedService.customer_profile.companies?.name}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {deleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mr-4">
                  <Trash2 className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Confirm Delete</h2>
                  <p className="text-sm text-gray-600">This action cannot be undone</p>
                </div>
              </div>

              <p className="text-gray-700 mb-6">
                Are you sure you want to delete this {deleteConfirm.type === 'job' ? 'job posting' : 'service requirement'}?
                {deleteConfirm.type === 'job' && ' This will remove it from the consultant opportunities page.'}
              </p>

              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  disabled={deleting}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (deleteConfirm.type === 'job') {
                      handleDeleteJob(deleteConfirm.id);
                    } else {
                      handleDeleteService(deleteConfirm.id);
                    }
                  }}
                  disabled={deleting}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center"
                >
                  {deleting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
